import { ChatMessageFormat } from 'types/enums/ChatMessageFormat';
import { ChatMatchMessage } from 'types/interfaces/chat/ChatMatchMessage';
import { ChatMessage } from 'types/interfaces/chat/ChatMessage';
import { ChatPrivatePhoto } from 'types/interfaces/chat/ChatPrivatePhoto';
import { User } from 'types/interfaces/user/User';

import { httpClient } from './httpClient';

export type Message = ChatMessage | ChatMatchMessage;
export interface Dialog {
  contact: User;
  last_message: Message;
  unread_count: number;
}

export interface DialogsResponse {
  data: Dialog[];
  next: string | null;
}

export interface MessagesResponse {
  contact: User;
  messages: Message[];
  next: string | null;
  // TODO: Define the type
  is_matched: boolean;
}

export interface WsTokenPayload {
  userId?: string;
}

interface SendTextMessagePayload {
  format:
    | ChatMessageFormat.Text
    | ChatMessageFormat.Video
    | ChatMessageFormat.MatchIcebreaker
    | ChatMessageFormat.Icebreaker
    | ChatMessageFormat.WantsChat;
  contactId: number;
  body: string;
}

interface SendPhotoMessagePayload {
  format: ChatMessageFormat.Photo;
  contactId: number;
  photoId: number;
}

interface SendStickerMessagePayload {
  format: ChatMessageFormat.Sticker;
  contactId: number;
  stickerId: number;
}

// explore
interface SendGiftMessagePayload {
  contactId: number;
  giftId: number;
}

const readIncomeMessage = (message: Message) => {
  if (!message.is_incoming) {
    return message;
  }

  return {
    ...message,
    read_at: new Date().toUTCString(),
  };
};

export const DialogsApi = {
  async fetchDialogs(url?: string | null) {
    const { data } = await httpClient.get<DialogsResponse>(url || '/dialogs');

    return data;
  },

  async fetchMessages(contactId: number) {
    const { data } = await httpClient.get<MessagesResponse>(
      `/dialogs/${contactId}`
    );

    return {
      ...data,
      messages: data.messages.map(readIncomeMessage),
    };
  },

  async fetchMoreMessages(url: string) {
    const { data } = await httpClient.get<MessagesResponse>(url);

    return data;
  },

  async sendTextMessage(payload: SendTextMessagePayload) {
    return httpClient.post<{ message: Message }>('/messages', {
      format: payload.format,
      body: payload.body,
      contact_id: payload.contactId,
    });
  },

  async sendPhotoMessage(payload: SendPhotoMessagePayload) {
    return httpClient.post<{ message: Message }>('/messages', {
      format: ChatMessageFormat.Photo,
      photo_id: payload.photoId,
      contact_id: payload.contactId,
    });
  },

  async sendStickerMessage(payload: SendStickerMessagePayload) {
    return httpClient.post<{ message: Message }>('/messages', {
      format: ChatMessageFormat.Sticker,
      sticker_id: payload.stickerId,
      contact_id: payload.contactId,
    });
  },

  async sendGiftMessage(payload: SendGiftMessagePayload) {
    return httpClient.post<{ message: Message }>('/messages', {
      format: ChatMessageFormat.Gift,
      contact_id: payload.contactId,
      gift_id: payload.giftId,
    });
  },

  async retrySendMessage(messageId: number) {
    const { data } = await httpClient.post<{ message: Message }>(
      `/messages/${messageId}/retry`
    );

    return data;
  },

  async fetchWsToken() {
    const { data } = await httpClient.get<{ token: string }>(
      '/broadcasting/token'
    );

    return data;
  },

  async uploadPrivatePhoto(payload: { photo: File }) {
    const formData = new FormData();
    formData.append('photo', payload.photo);

    return httpClient.post<ChatPrivatePhoto>(
      '/user/private-photo/upload',
      formData
    );
  },
};
